import { useEffect, useCallback, type ReactNode } from 'react';
import { useAppShellDocument } from '@/lib/app-shell';
import { Outlet, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { LayoutDashboard, Gamepad2, Users, User, LogOut, Bell, Layers, Mic } from 'lucide-react';
import { cn, CompassLogo } from '@jeraisonne/ui';
import { NavBadge } from '@/components/nav-badge';
import { useFeedUnreadCount } from '@/lib/news/use-role-news-feed';
import { useAuth } from '@/lib/auth';
import { SPEAKER_PAGE, useCanRecordVoices } from '@/lib/animations/speaker';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';

const LEFT_NAV = [
  { id: 'dashboard', label: 'Accueil', icon: LayoutDashboard, to: '/player/dashboard' },
  { id: 'news', label: 'Feed', icon: Bell, to: '/player/news' },
] as const;

const RIGHT_NAV = [
  { id: 'contacts', label: 'Mon réseau', icon: Users, to: '/player/contacts' },
  { id: 'profile', label: 'Profil', icon: User, to: '/profile' },
] as const;

const JOUER_NAV = { id: 'jouer', label: 'JeJoue', to: '/player/jouer' } as const;

const ALL_NAV = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, to: '/player/dashboard' },
  { id: 'news', label: 'Feed', icon: Bell, to: '/player/news' },
  { id: 'jouer', label: 'JeJoue', icon: Gamepad2, to: '/player/jouer' },
  { id: 'skillcards', label: 'Mes SKILLCARDS', icon: Layers, to: '/player/skillcards' },
  { id: 'contacts', label: 'Mon réseau', icon: Users, to: '/player/contacts' },
  { id: 'profile', label: 'Profil', icon: User, to: '/profile' },
] as const;

/** Voix des animations : comptes « speaker » (migration 0678). */
const SPEAKER_NAV = {
  id: 'speaker',
  label: 'Audios animations',
  icon: Mic,
  to: SPEAKER_PAGE,
} as const;

export function PlayerLayout() {
  const { profile, loading, profileLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!profile) {
      navigate({ to: '/onboarding' });
      return;
    }
    if (profile.role !== 'player') {
      navigate({ to: '/' });
    }
  }, [loading, profileLoading, profile, navigate]);

  if (loading || profileLoading || !profile || profile.role !== 'player') return null;

  return (
    <PlayerShell>
      <Outlet />
    </PlayerShell>
  );
}

/**
 * Coquille de l'espace Curieux (sidebar desktop + barre du bas mobile). Partagée
 * par les routes `/player/*` ET le Profil (`/profile`), pour que le Curieux ne
 * se retrouve jamais sur un écran sans navigation.
 */
export function PlayerShell({ children }: { children: ReactNode }) {
  const speaker = useCanRecordVoices();
  // La page ne défile pas (seul le contenu) : barre du bas stable après le clavier iOS.
  useAppShellDocument();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location.pathname });
  // Nouveautés du Feed depuis sa dernière ouverture (masqué sur la page Feed).
  const feedUnread = useFeedUnreadCount('player');
  const feedBadge = location.startsWith('/player/news') ? 0 : feedUnread;
  const queryClient = useQueryClient();

  const handleSignOut = useCallback(async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }, [navigate, queryClient]);

  const jouerActive = location.startsWith(JOUER_NAV.to);

  return (
    <div className="bg-ink-50 flex h-dvh overflow-hidden">
      {/* ── Desktop sidebar ──────────────────────────────────────────────── */}
      <aside className="border-ink-200 native:pt-[env(safe-area-inset-top)] native:pb-[env(safe-area-inset-bottom)] hidden w-60 shrink-0 flex-col border-r bg-white md:flex">
        <div className="px-5 pb-6 pt-5">
          <CompassLogo size="md" subtitle="Espace curieux" animated />
        </div>

        <nav className="flex flex-col gap-0.5 px-3">
          {[...ALL_NAV, ...(speaker ? [SPEAKER_NAV] : [])].map(({ id, label, icon: Icon, to }) => {
            const active = location.startsWith(to);
            const isJouer = id === 'jouer';
            return (
              <Link
                key={id}
                to={to}
                className={cn(
                  'font-display flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors',
                  isJouer
                    ? active
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                    : active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-ink-700 hover:bg-ink-100',
                )}
              >
                <Icon
                  className={cn(
                    'h-[18px] w-[18px] shrink-0',
                    isJouer
                      ? active
                        ? 'text-primary-700'
                        : 'text-primary-500'
                      : active
                        ? 'text-primary-600'
                        : 'text-ink-500',
                  )}
                />
                {label}
                {id === 'news' && <NavBadge count={feedBadge} />}
              </Link>
            );
          })}
        </nav>

        <div className="bg-ink-100 mx-3 mb-4 mt-auto rounded-xl p-3.5">
          <p className="font-display text-ink-500 mb-1 text-[11px] font-bold uppercase tracking-widest">
            Connecté en tant que
          </p>
          <p className="font-display text-ink-900 truncate text-[14px] font-extrabold leading-tight">
            {profile?.display_name ?? 'Curieux'}
          </p>
          <div className="mt-1.5 flex items-center justify-between">
            <Link to="/profile" className="text-primary-600 font-sans text-[11px] hover:underline">
              Mon profil
            </Link>
            <button
              onClick={handleSignOut}
              className="text-ink-500 hover:text-accent-600 flex items-center gap-1 font-sans text-[11px] transition-colors"
            >
              <LogOut className="h-3 w-3" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <main className="native:pt-[env(safe-area-inset-top)] native:md:pb-[env(safe-area-inset-bottom)]! flex-1 overflow-y-auto pb-20 md:pb-0">
          {children}
        </main>

        {/* ── Mobile bottom tab bar (JeJoue centré proéminent) ─────────── */}
        <nav className="border-ink-200 native:pb-[min(env(safe-area-inset-bottom),2.125rem)] native:h-[calc(3.5rem+min(env(safe-area-inset-bottom),2.125rem))] ocean:bg-[#061433] ocean:shadow-[0_-4px_20px_rgba(0,0,0,0.35)] fixed inset-x-0 bottom-0 z-10 flex h-[calc(3.5rem+env(safe-area-inset-bottom))] items-end border-t bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(5,102,242,0.08)] md:hidden">
          {LEFT_NAV.map(({ id, label, icon: Icon, to }) => {
            const active = location.startsWith(to);
            return (
              <Link
                key={id}
                to={to}
                className={cn(
                  'font-display flex flex-1 flex-col items-center gap-0.5 pb-2 pt-2.5 text-[10px] font-semibold transition-colors',
                  active ? 'text-primary-600' : 'text-ink-400',
                )}
              >
                <span className="relative">
                  <Icon className={cn('h-5 w-5', active ? 'text-primary-600' : 'text-ink-400')} />
                  {id === 'news' && <NavBadge count={feedBadge} variant="icon" />}
                </span>
                <span className="truncate">{label}</span>
              </Link>
            );
          })}

          {/* Centre : JeJoue — bouton FAB surélevé */}
          <div className="relative flex flex-1 flex-col items-center pb-2">
            <Link
              to={JOUER_NAV.to}
              className={cn(
                'absolute -top-5 flex h-14 w-14 flex-col items-center justify-center rounded-full shadow-lg transition-all active:scale-95',
                jouerActive
                  ? 'bg-primary-600 ring-primary-200 ring-4'
                  : 'bg-primary-500 hover:bg-primary-600',
              )}
              aria-label="JeJoue"
            >
              <Gamepad2 className="h-7 w-7 text-white" />
            </Link>
            <span
              className={cn(
                'font-display mt-10 text-[10px] font-semibold',
                jouerActive ? 'text-primary-600' : 'text-ink-400',
              )}
            >
              JeJoue
            </span>
          </div>

          {RIGHT_NAV.map(({ id, label, icon: Icon, to }) => {
            const active = location.startsWith(to);
            return (
              <Link
                key={id}
                to={to}
                className={cn(
                  'font-display flex flex-1 flex-col items-center gap-0.5 pb-2 pt-2.5 text-[10px] font-semibold transition-colors',
                  active ? 'text-primary-600' : 'text-ink-400',
                )}
              >
                <Icon className={cn('h-5 w-5', active ? 'text-primary-600' : 'text-ink-400')} />
                <span className="truncate">{label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
